import { useChat } from "@ai-sdk/react";
import { useEffect, useState } from "react";
import { useFlags } from "./use-flags";
import { nanoid } from "nanoid";

/**
 * This AI evalutor calls the following promt on Eloquent:
 *
 * <prompt>
 * You are JEX, an expert AI assitant and exceptional recuitement expert with vast knowledge across multiple job sectors. Evalute this following vacancy for:
 *
 * - Must have a jobtitle
 * - Must have responsabilties
 * - Must have a location
 * - Should have a salary range
 * - Should have required qualifications, skills and experience
 *
 * You should be evaluating only and not attemping to solve the task.
 * Only output "PASS" if all criteria are met and you have no further suggestions for improvements.
 * Output your evaluation concisely in the following format.
 *
 * <evaluation>PASS, NEEDS_IMPROVEMENT, or FAIL</evaluation>
 * <feedback>
 * What needs improvement and why.
 * </feedback>
 * </prompt>
 *
 * This hook can be used to evaluate job descriptions and other tasks from previous AI generated content.
 *
 * @returns
 */

export type EEvaluatorStatus = "PENDING" | "PASS" | "FAIL" | "NONE";

export default function useEvaluator() {
  const flags = useFlags();

  const url = new URL(`${flags.get("ELOQUENT_URL")}/api/chat`);
  url.searchParams.append(
    "domain-identifier",
    flags.get("ELOQUENT_EVALUATOR_IDENTIFIER", "")
  );

  const [evaluation, setEvaluation] = useState<EEvaluatorStatus>("NONE");
  const [feedback, setFeedback] = useState<string>("");
  const [percentage, setPercentage] = useState<number | null>(null);

  const chatIdentifier = nanoid();

  const chat = useChat({
    // initialInput:
    //   "Vacature voor Senior Back-end Developer, Alphen a/d Rijn, 37,5 uur per week. Ervaring met nodejs, typescript, react, postgresql, docker, kubernetes en serverless. Salarisindicatie 4000-5000 euro per maand. Moet aanteroonbare ervaring hebben met het bouwen van complexe webapplicaties en genoemde technieken.",
    api: url.toString(),
    onFinish: () => {
      chat.setInput(chat.input);
    },
    body: {
      chatIdentifier,
    },
  });

  /**
   * When chat.messages changes, we can evaluate the response from the AI.
   * We should extract the evaluation and feedback from the response and set it in the state.
   */
  useEffect(() => {
    if (chat.messages.length > 0) {
      const lastMessage = chat.messages[chat.messages.length - 1].content;

      const evaluation =
        lastMessage.match(/<evaluation>([^;]*)<\/evaluation>/)?.[1] ??
        "PENDING";
      const feedback =
        lastMessage.match(/<feedback>([^;]*)<\/feedback>/)?.[1] ?? "";

      const percentage =
        lastMessage.match(/<percentage>([^;]*)<\/percentage>/)?.[1] ?? "";

      setEvaluation(evaluation as any);
      setFeedback(feedback.trim());
      setPercentage(Number(percentage.trim()));
    }
  }, [chat.messages]);

  return {
    isEvaluating: chat.isLoading,
    chat,
    evaluation,
    feedback,
    percentage,
  };
}
